import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const FILTERS = [
  { id: "enhance", label_es: "Color realzado", label_en: "Enhanced colour" },
  { id: "bw", label_es: "B/N escáner", label_en: "B&W scanner" },
  { id: "none", label_es: "Original", label_en: "Original" },
];

const MAX_PAGES = 30;

function fileToObjectURL(file) {
  return URL.createObjectURL(file);
}

function ScanPage({ page, index, total, onRotate, onRemove, onMoveUp, onMoveDown }) {
  return (
    <div
      className="border-2 border-[#1C1917] bg-white shadow-[3px_3px_0_0_#1C1917] overflow-hidden"
      data-testid={`scan-page-${index}`}
    >
      <div
        className="relative w-full bg-[#F5F5F4]"
        style={{ aspectRatio: "3 / 4" }}
      >
        <img
          src={page.preview}
          alt={`Página ${index + 1}`}
          className="absolute inset-0 w-full h-full object-contain"
          style={{ transform: `rotate(${page.rotation}deg)`, transition: "transform .15s ease" }}
        />
        <div className="absolute top-2 left-2 bg-[#1C1917] text-[#FFC53D] px-2 py-1 text-[10px] font-black tracking-widest uppercase">
          Pág {index + 1}/{total}
        </div>
      </div>
      <div className="border-t-2 border-[#1C1917] grid grid-cols-4">
        <button
          onClick={() => onRotate(index)}
          className="h-10 border-r-2 border-[#1C1917] font-black text-[10px] tracking-widest uppercase hover:bg-[#FFC53D]"
          title="Rotar 90°"
          data-testid={`scan-rotate-${index}`}
        >
          Rotar
        </button>
        <button
          onClick={() => onMoveUp(index)}
          disabled={index === 0}
          className="h-10 border-r-2 border-[#1C1917] font-black text-[10px] tracking-widest uppercase hover:bg-[#FFC53D] disabled:opacity-30 disabled:hover:bg-white"
          data-testid={`scan-up-${index}`}
        >
          ↑
        </button>
        <button
          onClick={() => onMoveDown(index)}
          disabled={index === total - 1}
          className="h-10 border-r-2 border-[#1C1917] font-black text-[10px] tracking-widest uppercase hover:bg-[#FFC53D] disabled:opacity-30 disabled:hover:bg-white"
          data-testid={`scan-down-${index}`}
        >
          ↓
        </button>
        <button
          onClick={() => onRemove(index)}
          className="h-10 font-black text-[10px] tracking-widest uppercase hover:bg-[#E5484D] hover:text-white"
          data-testid={`scan-remove-${index}`}
        >
          Eliminar
        </button>
      </div>
    </div>
  );
}

export default function DocScan() {
  const { t, i18n } = useTranslation();
  const isEs = (i18n.language || "es").startsWith("es");
  const [pages, setPages] = useState([]); // {file, preview, rotation}
  const [filter, setFilter] = useState("enhance");
  const [ocr, setOcr] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => { pages.forEach((p) => URL.revokeObjectURL(p.preview)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const remaining = MAX_PAGES - pages.length;
    if (remaining <= 0) {
      toast.error(isEs ? `Máximo ${MAX_PAGES} páginas` : `Max ${MAX_PAGES} pages`);
      return;
    }
    const accepted = arr
      .filter((f) => (f.type || "").startsWith("image/") || /\.(heic|heif|bmp|tif|tiff)$/i.test(f.name))
      .slice(0, remaining)
      .map((f) => ({ file: f, preview: fileToObjectURL(f), rotation: 0 }));
    if (!accepted.length) {
      toast.error(isEs ? "Solo se permiten imágenes" : "Images only");
      return;
    }
    setPages((prev) => [...prev, ...accepted]);
    setResult(null);
  };

  const rotate = (idx) => setPages((prev) =>
    prev.map((p, i) => (i === idx ? { ...p, rotation: (p.rotation + 90) % 360 } : p))
  );
  const removePage = (idx) => setPages((prev) => {
    URL.revokeObjectURL(prev[idx].preview);
    return prev.filter((_, i) => i !== idx);
  });
  const move = (idx, dir) => setPages((prev) => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const clearAll = () => {
    pages.forEach((p) => URL.revokeObjectURL(p.preview));
    setPages([]);
    setResult(null);
  };

  const run = async () => {
    if (!pages.length) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      pages.forEach((p) => fd.append("files", p.file, p.file.name || "page.jpg"));
      fd.append("filter", filter);
      fd.append("ocr", ocr ? "true" : "false");
      fd.append("rotations", pages.map((p) => p.rotation).join(","));
      if (name.trim()) fd.append("name", name.trim());
      const { data } = await api.post("/pdf/scan", fd);
      setResult(data);
      toast.success(isEs ? "PDF generado" : "PDF ready");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="doc-scan-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">
        ← {t("cta_back")}
      </Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2" data-testid="doc-scan-title">
        {t("tool_scan")}
      </h1>
      <p className="text-zinc-500 mt-2">{t("tool_scan_desc")}</p>

      {/* Capture / upload controls */}
      <div className="mt-8 border-2 border-[#1C1917] bg-white p-5 shadow-[3px_3px_0_0_#1C1917]">
        <div className="flex flex-wrap gap-3 items-center">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            className="hidden"
            data-testid="scan-camera-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif,.bmp,.tif,.tiff"
            multiple
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            className="hidden"
            data-testid="scan-file-input"
          />
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="h-12 px-5 border-2 border-[#1C1917] bg-[#FFC53D] text-[#1C1917] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917]"
            data-testid="scan-take-photo"
          >
            📷 {isEs ? "Hacer foto" : "Take photo"}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="h-12 px-5 border-2 border-[#1C1917] bg-white text-[#1C1917] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917]"
            data-testid="scan-upload-images"
          >
            {isEs ? "Subir imágenes" : "Upload images"}
          </button>
          {pages.length > 0 && (
            <button
              onClick={clearAll}
              className="h-12 px-4 border-2 border-[#1C1917] bg-white text-[#1C1917] font-black text-xs tracking-widest uppercase hover:bg-[#E5484D] hover:text-white ml-auto"
              data-testid="scan-clear-all"
            >
              {isEs ? "Limpiar" : "Clear"}
            </button>
          )}
        </div>

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <span className="eyebrow text-[10px]">{isEs ? "Filtro" : "Filter"}</span>
            <div className="mt-1 inline-flex border-2 border-[#1C1917] w-full" data-testid="scan-filter">
              {FILTERS.map((f, i) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`flex-1 h-11 text-[10px] font-black tracking-widest uppercase ${i > 0 ? "border-l-2 border-[#1C1917]" : ""} ${
                    filter === f.id ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
                  }`}
                  data-testid={`scan-filter-${f.id}`}
                >
                  {isEs ? f.label_es : f.label_en}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="eyebrow text-[10px]">{isEs ? "Nombre del PDF" : "PDF name"}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isEs ? "Ej: DNI Juan" : "E.g. scan1"}
              className="mt-1 w-full h-11 px-3 border-2 border-[#1C1917] bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
              data-testid="scan-name"
            />
          </div>
          <label className="flex items-center gap-3 border-2 border-[#1C1917] bg-white px-4 h-11 self-end cursor-pointer hover:bg-[#FFC53D]">
            <input
              type="checkbox"
              checked={ocr}
              onChange={(e) => setOcr(e.target.checked)}
              className="w-4 h-4 accent-[#1C1917]"
              data-testid="scan-ocr-toggle"
            />
            <span className="text-xs font-black tracking-widest uppercase">
              {isEs ? "OCR (ES+EN)" : "OCR (ES+EN)"}
            </span>
          </label>
        </div>
      </div>

      {/* Pages grid */}
      {pages.length === 0 ? (
        <div
          className="mt-6 border-2 border-dashed border-[#1C1917] bg-white p-10 text-center"
          data-testid="scan-empty"
        >
          <div className="text-5xl">📄</div>
          <p className="mt-3 text-sm text-[#57534E] font-bold">
            {isEs
              ? "Haz una foto o sube imágenes (JPG, PNG, HEIC). Máximo 30 páginas."
              : "Take a photo or upload images (JPG, PNG, HEIC). Max 30 pages."}
          </p>
        </div>
      ) : (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <span className="eyebrow text-[10px]">
              {pages.length} {isEs ? "página(s)" : "page(s)"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="scan-pages">
            {pages.map((p, i) => (
              <ScanPage
                key={p.preview}
                page={p}
                index={i}
                total={pages.length}
                onRotate={rotate}
                onRemove={removePage}
                onMoveUp={(idx) => move(idx, -1)}
                onMoveDown={(idx) => move(idx, 1)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Action bar */}
      {pages.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <button
            onClick={run}
            disabled={busy}
            className="h-12 px-6 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#FFC53D] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#FFC53D] disabled:opacity-60"
            data-testid="scan-generate"
          >
            {busy
              ? (isEs ? "Generando…" : "Generating…")
              : (isEs ? "Generar PDF" : "Generate PDF")}
          </button>
          {busy && ocr && (
            <span className="text-xs text-[#57534E] uppercase tracking-wider font-bold">
              {isEs ? "Aplicando OCR, esto puede tardar unos segundos…" : "Running OCR, this may take a few seconds…"}
            </span>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div
          className="mt-8 border-2 border-[#1C1917] bg-[#46A758] text-white p-5 shadow-[6px_6px_0_0_#1C1917] flex flex-wrap items-center gap-4"
          data-testid="scan-result"
        >
          <div className="flex-1">
            <span className="eyebrow text-[10px] text-[#FFC53D]">
              {isEs ? "Listo" : "Done"}
            </span>
            <div className="display text-xl mt-1">{result.original_filename}</div>
            <div className="text-xs opacity-90">
              {(result.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button
            onClick={() => downloadFile(result.id, result.original_filename)}
            className="h-12 px-5 border-2 border-[#1C1917] bg-white text-[#1C1917] font-black text-xs tracking-widest uppercase hover:bg-[#FFC53D]"
            data-testid="scan-download"
          >
            {isEs ? "Descargar" : "Download"}
          </button>
          <Link
            to="/dashboard"
            className="h-12 px-4 inline-flex items-center border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] font-black text-xs tracking-widest uppercase hover:bg-black"
            data-testid="scan-go-library"
          >
            {isEs ? "Biblioteca" : "Library"}
          </Link>
        </div>
      )}
    </div>
  );
}
